import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const Vendors = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'warning'|'error', message: string }


  const [vendors, setVendors] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/admin/vendors');
      if (response.data.success) {
        setVendors(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchVendorCategories();
    fetchCentres();
  }, []);

  const fetchCentres = async () => {
    try {
      const response = await api.get('/admin/centres');
      if (response.data.success) {
        setCentres(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch centres:", error);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    type: '',
    status: 'Active',
    permissions: [],
    modules: ['livestream'],
    centreIds: []  // multi-centre array
  });

  const availableModules = [
    { id: 'livestream', name: 'Live Stream' },
    { id: 'donations', name: 'Donations' },
    { id: 'ceremonies', name: 'Ceremony Services' },
    { id: 'sevaOffering', name: 'SevaOffering' },
    { id: 'products', name: 'Products' },
    { id: 'slides', name: 'Slide Creator' },
    { id: 'pages', name: 'Dynamic Pages' },
    { id: 'system', name: 'System Panel' },
  ];

  const [vendorCategories, setVendorCategories] = useState([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const availablePermissions = [
    'Dashboard', 'Centres', 'Categories', 'Ceremony Services',
    'SevaOffering', 'Donations', 'Notifications', 'Live Streams',
    'Subscriptions', 'Vendors', 'Products', 'Orders',
    'Users', 'Pages', 'My Wallet'
  ];

  const fetchVendorCategories = async () => {
    try {
      const response = await api.get('/admin/vendor-categories');
      if (response.data.success) {
        setVendorCategories(response.data.data);
        if (response.data.data.length > 0) {
          setFormData(prev => ({ ...prev, type: prev.type || response.data.data[0].name }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch vendor categories:", error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName) return;
    try {
      const response = await api.post('/admin/vendor-categories', { name: newCategoryName });
      if (response.data.success) {
        setVendorCategories([...vendorCategories, response.data.data]);
        setFormData({ ...formData, type: response.data.data.name });
        setNewCategoryName('');
        setIsAddingCategory(false);
      }
    } catch (error) {
      alert("Failed to add category: " + (error.response?.data?.message || error.message));
    }
  };

  const handlePermissionChange = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEditVendor = (vendor) => {
    setEditMode(true);
    setSelectedId(vendor._id);
    // Build centreIds array from populated centres
    const assignedCentreIds = (vendor.centres || []).map(t => t._id || t);
    setFormData({
      name:        vendor.userId?.name || vendor.name,
      shopName:    vendor.shopName || vendor.name || '',
      email:       vendor.userId?.email || '',
      password:    '',
      type:        vendor.type || '',
      status:      vendor.approved ? 'Active' : 'Pending Review',
      permissions: vendor.userId?.permissions || [],
      modules:     vendor.modules || [],
      centreIds:   assignedCentreIds
    });
    setIsAddModalOpen(true);
  };

  const handleSaveVendor = async () => {
    if (!formData.name || !formData.email || (!editMode && !formData.password)) {
      alert("Please enter Name, Email, and Password.");
      return;
    }

    try {
      if (editMode) {
        const response = await api.put('/admin/vendors/' + selectedId, formData);
        if (response.data.success) {
          setToast({ type: 'success', message: `✅ ${formData.name} updated successfully!` });
          setIsAddModalOpen(false);
          resetForm();
          fetchVendors();
        }
      } else {
        const response = await api.post('/admin/vendors', formData);
        if (response.data.success) {
          setToast({ type: 'success', message: `✅ ${formData.name} added successfully!` });
          setIsAddModalOpen(false);
          resetForm();
          fetchVendors();
        }
      }
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.message || error.response?.data?.details || error.message;

      if (status === 400 && msg.toLowerCase().includes('already exists')) {
        setToast({ type: 'warning', message: '⚠️ An account with this email already exists. Please try a different email.' });
      } else {
        setToast({ type: 'error', message: `❌ Failed to save vendor: ${msg}` });
      }
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setSelectedId(null);
    setFormData({ name: '', shopName: '', email: '', password: '', type: vendorCategories[0]?.name || '', status: 'Active', permissions: [], modules: ['livestream'], centreIds: [] });
  };

  const handleModuleToggle = (moduleId) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleId)
        ? prev.modules.filter(m => m !== moduleId)
        : [...prev.modules, moduleId]
    }));
  };

  const handleCentreToggle = (centreId) => {
    setFormData(prev => ({
      ...prev,
      centreIds: prev.centreIds.includes(centreId)
        ? prev.centreIds.filter(t => t !== centreId)
        : [...prev.centreIds, centreId]
    }));
  };

  const handleDeleteVendor = async (vendorId) => {
    console.log("Attempting to delete ID:", vendorId);

    try {
      const response = await api.delete('/admin/vendors/' + vendorId);
      if (response.data.success) {
        setToast({ type: 'success', message: '✅ Vendor deleted successfully!' });
        fetchVendors();
      }
    } catch (error) {
      setToast({ type: 'error', message: `❌ Failed to delete vendor: ${error.response?.data?.message || error.message}` });
    }
  };

  return (
    <div className="p-8 relative">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-6 right-6 z-[200] max-w-md px-5 py-4 rounded-xl shadow-2xl border flex items-start gap-3 animate-slide-in ${
          toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
          toast.type === 'warning' ? 'bg-amber-50 border-amber-300 text-amber-900' :
          'bg-red-50 border-red-200 text-red-800'
        }`}>
          <span className="material-symbols-outlined text-lg mt-0.5">
            {toast.type === 'success' ? 'check_circle' : toast.type === 'warning' ? 'warning' : 'error'}
          </span>
          <div className="flex-1">
            <p className="text-sm font-bold leading-snug">{toast.message}</p>
          </div>
          <button onClick={() => setToast(null)} className="text-current opacity-50 hover:opacity-100 transition-opacity">
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>
      )}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="font-serif text-3xl font-bold text-[#2A2858]">Centre Vendors</h2>
          <p className="mt-1 text-stone-500 text-sm">Manage Pundits, Acaryas, and Stores</p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#2A2858] text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg hover:bg-[#14122B] transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-sm font-bold">person_add</span> Add Vendor
        </button>
      </div>

      <div className="bg-white border border-[#E8B84B]/20 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#FAF9F6] border-b border-[#E8B84B]/20 text-xs">
              <th className="px-8 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Vendor ID</th>
              <th className="px-8 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Shop / Owner</th>
              <th className="px-8 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Category</th>
              <th className="px-8 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Rating</th>
              <th className="px-8 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Status</th>
              <th className="px-8 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-8 py-8 text-center text-stone-500 font-bold">Loading vendors...</td>
              </tr>
            ) : vendors.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-8 py-8 text-center text-stone-500">No vendors found.</td>
              </tr>
            ) : vendors.map((vendor, index) => {
              const vendorId = vendor._id ? `V-${vendor._id.slice(-6).toUpperCase()}` : `V-${index}`;
              const shopName = vendor.shopName || vendor.name || 'Unnamed Shop';
              const ownerName = vendor.userId?.name || 'Unknown Owner';
              const type = vendor.type || 'Vendor';
              const rating = vendor.rating || 0.0;
              const status = vendor.approved || vendor.status === 'Active' ? 'Active' : 'Pending Review';
              const badgeClass = status === 'Active'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-amber-50 text-amber-700 border-amber-200';

              return (
                <tr key={vendor._id || index} className="hover:bg-stone-50 transition-colors">
                  <td className="px-8 py-5 font-bold text-stone-700">{vendorId}</td>
                  <td className="px-8 py-5">
                    <div className="font-bold text-stone-800">{shopName}</div>
                    <div className="text-[10px] text-stone-500 font-bold uppercase tracking-wider">{ownerName}</div>
                  </td>
                  <td className="px-8 py-5 text-stone-600">{type}</td>
                  <td className="px-8 py-5 flex items-center gap-1 text-stone-700 font-bold">
                    <span className="material-symbols-outlined text-[#E8B84B] text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span> {rating.toFixed(1)}
                  </td>
                  <td className="px-8 py-5">
                    <span className={`px-4 py-1.5 rounded-full text-xs font-bold border ${badgeClass}`}>
                      {status}
                    </span>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex gap-3">
                      <button 
                        onClick={() => handleEditVendor(vendor)}
                        className="text-stone-400 hover:text-blue-500 transition-colors"
                        title="Edit Vendor"
                      >
                        <span className="material-symbols-outlined text-xl">edit</span>
                      </button>
                      <button onClick={() => handleDeleteVendor(vendor._id || vendor.id)} className="text-stone-400 hover:text-red-500 transition-colors">
                        <span className="material-symbols-outlined text-xl">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Vendor Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-fade-in max-h-[90vh] flex flex-col">
            <div className="bg-[#FAF9F6] border-b border-[#E8B84B]/20 px-6 py-4 flex justify-between items-center shrink-0">
              <h3 className="font-serif text-xl font-bold text-[#2A2858]">{editMode ? 'Edit Vendor Details' : 'Register New Vendor'}</h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-stone-400 hover:text-red-500 transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <form className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Full Name (Owner)</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Acharya Rahul"
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8B84B]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Shop / Brand Name</label>
                <input
                  type="text"
                  name="shopName"
                  value={formData.shopName || ''}
                  onChange={handleInputChange}
                  placeholder="e.g. Rahul Astro Services"
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8B84B]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="e.g. rahul@example.com"
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8B84B]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Secure password"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:border-[#E8B84B]"
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(v => !v)} 
                    style={{ 
                      position: 'absolute', 
                      right: '0.75rem', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer', 
                      color: '#666',
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0
                    }}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1 flex justify-between items-center">
                  Category
                  <button
                    type="button"
                    onClick={() => setIsAddingCategory(!isAddingCategory)}
                    className="text-[10px] text-[#2A2858] hover:underline font-bold"
                  >
                    {isAddingCategory ? '✕ Cancel' : '+ New'}
                  </button>
                </label>

                {isAddingCategory ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="New category name"
                      className="flex-1 border border-stone-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#E8B84B]"
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      className="bg-[#E8B84B] text-white px-4 rounded-lg font-bold text-xs"
                    >
                      Add
                    </button>
                  </div>
                ) : (
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8B84B]"
                  >
                    {vendorCategories.map(cat => (
                      <option key={cat._id} value={cat.name}>{cat.name}</option>
                    ))}
                    {vendorCategories.length === 0 && <option value="">No categories available</option>}
                  </select>
                )}
              </div>
              
              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">
                  Assign Centres
                  {formData.centreIds.length > 0 && (
                    <span className="ml-2 bg-[#2A2858] text-white text-[9px] font-black px-2 py-0.5 rounded-full">
                      {formData.centreIds.length} selected
                    </span>
                  )}
                </label>
                {centres.length === 0 ? (
                  <p className="text-xs text-stone-400 italic px-1">No centres available</p>
                ) : (
                  <div className="border border-stone-200 rounded-lg overflow-hidden overflow-y-auto" style={{ maxHeight: 160 }}>
                    {centres.map(centre => {
                      const tid = centre._id;
                      const checked = formData.centreIds.includes(tid);
                      return (
                        <label
                          key={tid}
                          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors hover:bg-stone-50 ${checked ? 'bg-amber-50' : ''}`}
                          style={{ borderBottom: '1px solid #f5f5f4' }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleCentreToggle(tid)}
                            className="w-3.5 h-3.5 accent-[#2A2858]"
                          />
                          <span className={`text-sm font-medium ${checked ? 'text-[#2A2858]' : 'text-stone-700'}`}>
                            🛕 {centre.name}
                          </span>
                          {centre.location && (
                            <span className="text-xs text-stone-400 ml-auto truncate max-w-[100px]">{centre.location}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-[0.2em] mb-2">Service Related Modules</label>
                  <div className="grid grid-cols-2 gap-2 bg-stone-50 p-4 rounded-xl border border-stone-100">
                    {availableModules.filter(m => !['slides', 'pages', 'system'].includes(m.id)).map(module => (
                      <label key={module.id} className="flex items-center gap-2 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={formData.modules.includes(module.id)}
                          onChange={() => handleModuleToggle(module.id)}
                          className="w-3.5 h-3.5 rounded border-stone-300 text-[#2A2858] focus:ring-[#2A2858]"
                        />
                        <span className="text-[10px] text-stone-600 group-hover:text-stone-900 transition-colors font-bold">{module.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-[0.2em] mb-2">Web & Maintenance</label>
                  <div className="grid grid-cols-2 gap-2 bg-stone-50 p-4 rounded-xl border border-stone-100">
                    {availableModules.filter(m => ['slides', 'pages', 'system'].includes(m.id)).map(module => (
                      <label key={module.id} className="flex items-center gap-2 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={formData.modules.includes(module.id)}
                          onChange={() => handleModuleToggle(module.id)}
                          className="w-3.5 h-3.5 rounded border-stone-300 text-[#2A2858] focus:ring-[#2A2858]"
                        />
                        <span className="text-[10px] text-stone-600 group-hover:text-stone-900 transition-colors font-bold">{module.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Detailed Permissions</label>
                <div className="bg-stone-50 p-4 rounded-xl border border-stone-100 max-h-[120px] overflow-y-auto custom-scrollbar">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {availablePermissions.map(perm => (
                      <label key={perm} className="flex items-center gap-2 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={formData.permissions.includes(perm)}
                          onChange={() => handlePermissionChange(perm)}
                          className="w-3.5 h-3.5 rounded border-stone-300 text-[#2A2858] focus:ring-[#2A2858]"
                        />
                        <span className="text-[10px] text-stone-500 group-hover:text-stone-900 transition-colors">{perm}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Initial Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8B84B]"
                >
                  <option value="Active">Active</option>
                  <option value="Pending Review">Pending Review</option>
                </select>
              </div>

              <div className="pt-6 flex gap-4">
                <button type="button" onClick={() => setIsAddModalOpen(false)} className="w-1/2 bg-stone-100 text-stone-600 font-bold py-2.5 rounded-lg hover:bg-stone-200 transition-colors">
                  Cancel
                </button>
                <button type="button" onClick={handleSaveVendor} className="w-1/2 bg-[#2A2858] text-white font-bold py-2.5 rounded-lg hover:bg-[#14122B] transition-colors shadow-lg">
                  {editMode ? 'Update Vendor' : 'Register Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors;